import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Select } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/queryKeys.js';
import { useTransfers } from '../transfers/api.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';
import { TransferRow } from '../transfers/TransferRow.js';

const SEND_CURRENCIES = ['EUR', 'USD', 'CAD'] as const;
const RECEIVE_CURRENCY = 'XAF';

interface RateRow {
  from_currency: string;
  to_currency: string;
  rate: number;
  updated_at: string;
}

interface QuoteResponse {
  rate: number;
  fee: number;
  amount_send: number;
  amount_receive: number;
  has_dynamic_tiers?: boolean;
}

export function HomePage() {
  const { t } = useTranslation();
  const transfersQ = useTransfers({ per_page: 4 });
  const [sendCurrency, setSendCurrency] = useState<(typeof SEND_CURRENCIES)[number]>('EUR');
  const [sendAmount, setSendAmount] = useState('');
  const [receiveAmountStr, setReceiveAmountStr] = useState('');
  const [activeField, setActiveField] = useState<'send' | 'receive'>('send');

  const ratesQ = useQuery({
    queryKey: qk.rates,
    queryFn: () => callFunction<{ rates: RateRow[] }>('rates-list'),
  });

  // Get the static rate for inverse calculation
  const staticRate =
    ratesQ.data?.rates.find(
      (rate) => rate.from_currency === sendCurrency && rate.to_currency === RECEIVE_CURRENCY,
    )?.rate ?? 0;

  // Sync send amount when receive field is edited
  const effectiveSendAmount =
    activeField === 'send'
      ? sendAmount
      : receiveAmountStr && staticRate > 0
        ? String(Math.round((Number(receiveAmountStr) / staticRate) * 100) / 100)
        : sendAmount;

  const numericAmount = Number(effectiveSendAmount || '0');
  const quoteQ = useQuery({
    queryKey: ['home-quote', sendCurrency, numericAmount],
    queryFn: () =>
      callFunction<QuoteResponse>('rates-quote', {
        body: { from_currency: sendCurrency, to_currency: RECEIVE_CURRENCY, amount: numericAmount },
      }),
    enabled: Number.isFinite(numericAmount) && numericAmount > 0,
  });

  const currentRate = quoteQ.data?.rate ?? staticRate;

  // Minimum send amount: equivalent of 10€
  const MIN_SEND_EUR = 10;
  const isBelowMinimum =
    Number(effectiveSendAmount) > 0 && Number(effectiveSendAmount) < MIN_SEND_EUR;

  const displayedSendAmount = activeField === 'send' ? sendAmount : effectiveSendAmount;
  const displayedReceiveAmount =
    activeField === 'receive'
      ? receiveAmountStr
      : quoteQ.data?.amount_receive != null
        ? String(quoteQ.data.amount_receive)
        : '';

  const sendOptions = useMemo(
    () =>
      SEND_CURRENCIES.map((currency) => ({
        code: currency,
        badge: currency === 'EUR' ? '🇪🇺' : currency === 'USD' ? '🇺🇸' : '🇨🇦',
      })),
    [],
  );

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-8">
      <section className="rounded-[2rem] border border-border/70 bg-[linear-gradient(180deg,hsl(var(--color-surface))_0%,hsl(var(--color-surface-elevated))_100%)] shadow-[0_28px_80px_hsl(var(--color-text)/0.06)]">
        <div className="space-y-5 px-5 py-5 sm:px-7 sm:py-6">
          <FieldCard
            label={t('transfers.youSend')}
            amount={displayedSendAmount}
            trailing={
              <CurrencySelect
                value={sendCurrency}
                options={sendOptions}
                onChange={(value) => setSendCurrency(value as (typeof SEND_CURRENCIES)[number])}
              />
            }
            onChange={(v) => {
              setActiveField('send');
              setSendAmount(v);
            }}
          />

          <div className="flex justify-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow">
              <DownArrowIcon />
            </div>
          </div>

          <FieldCard
            label={t('transfers.theyReceive')}
            amount={displayedReceiveAmount}
            trailing={<StaticCurrency code={RECEIVE_CURRENCY} badge="🇨🇲" />}
            onChange={(v) => {
              setActiveField('receive');
              setReceiveAmountStr(v);
            }}
          />

          <div className="rounded-[1.5rem] bg-white/70 px-5 py-4 text-center shadow-sm">
            <p className="text-base text-text-muted sm:text-lg">
              {t('transfers.rate')}{' '}
              <span className="font-semibold text-primary">
                1 {sendCurrency} = {formatNumber(currentRate, 0)} {RECEIVE_CURRENCY}
              </span>
            </p>
            <p className="mt-2 text-base text-text-muted sm:text-lg">
              {t('transfers.fee')}{' '}
              <span className="font-semibold text-text">
                {formatNumber(quoteQ.data?.fee ?? 0, 2)} {sendCurrency}
              </span>
            </p>
            {isBelowMinimum ? (
              <p className="text-sm text-danger">
                {t('transfers.minimumAmount', { amount: MIN_SEND_EUR, currency: 'EUR' })}
              </p>
            ) : null}
            {quoteQ.data?.has_dynamic_tiers ? (
              <div className="mt-3 text-sm text-text-muted">
                <p className="mt-1 text-xs text-primary/70">
                  <span className="inline-block rounded-pill bg-primary/10 px-2 py-0.5 font-medium">
                    ⓘ {t('landing.estimateHintTiered')}
                  </span>
                </p>
              </div>
            ) : null}
          </div>

          <Link
            to="/transfers/new"
            state={{
              amount: effectiveSendAmount,
              from_currency: sendCurrency,
            }}
            className={`block ${isBelowMinimum ? 'pointer-events-none' : ''}`}
          >
            <Button
              className="h-14 w-full text-lg font-semibold tracking-normal sm:h-16 sm:text-xl"
              disabled={isBelowMinimum}
            >
              {t('home.sendCta')}
            </Button>
          </Link>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-primary sm:text-3xl">
            {t('home.recentTransfers')}
          </h2>
          <Link to="/transfers" className="text-sm font-medium text-text-muted">
            {t('home.viewAll')}
          </Link>
        </div>
        {transfersQ.isLoading ? (
          <LoadingScreen />
        ) : transfersQ.isError ? (
          <ErrorState onRetry={() => transfersQ.refetch()} />
        ) : transfersQ.data?.items.length === 0 ? (
          <EmptyState title={t('home.noTransfers')} />
        ) : (
          <ul className="flex flex-col gap-1">
            {transfersQ.data?.items.map((transfer) => (
              <li key={transfer.id}>
                <TransferRow transfer={transfer} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function FieldCard({
  label,
  amount,
  trailing,
  onChange,
}: {
  label: string;
  amount: string;
  trailing: ReactNode;
  onChange?: (value: string) => void;
}) {
  return (
    <div className="rounded-[1.75rem] border border-border/70 bg-white px-5 py-4 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-text-muted sm:text-base">{label}</p>
        <div className="shrink-0">{trailing}</div>
      </div>
      <input
        value={amount}
        onChange={(event) => onChange?.(event.target.value.replace(/[^\d.]/g, ''))}
        placeholder="0.00"
        inputMode="decimal"
        className="mt-2 w-full bg-transparent text-5xl font-extrabold tracking-tight text-text outline-none placeholder:text-text-muted/40 sm:text-6xl"
      />
    </div>
  );
}

function CurrencySelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ code: string; badge: string }>;
  onChange: (value: string) => void;
}) {
  const selected = options.find((o) => o.code === value);

  return (
    <div className="flex items-center gap-2">
      <Select
        value={value}
        onChange={onChange}
        options={options.map((o) => ({ value: o.code, label: o.code }))}
        className="min-w-[5rem]"
      />
      {selected && (
        <span className="inline-flex text-base leading-none">
          {selected.badge}
        </span>
      )}
    </div>
  );
}

function StaticCurrency({ code, badge }: { code: string; badge: string }) {
  return (
    <div className="inline-flex items-center gap-2 text-lg font-semibold text-text">
      <span className="inline-flex text-base leading-none">{badge}</span>
      <span>{code}</span>
    </div>
  );
}

function DownArrowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 4V16M10 16L5 11M10 16L15 11"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatNumber(value: number, digits: number) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}
